import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

const PREF_KEY = "soul-ambient-on";
const CHORD = [110, 164.81, 220, 277.18, 329.63];

export const AmbientSoundToggle = () => {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const stop = () => {
    const a = audioRef.current;
    if (!a) return;
    const t = a.ctx.currentTime;
    a.master.gain.cancelScheduledValues(t);
    a.master.gain.setValueAtTime(a.master.gain.value, t);
    a.master.gain.linearRampToValueAtTime(0, t + 1.5);
    setTimeout(() => { try { a.ctx.close(); } catch (e) {} }, 1700);
    audioRef.current = null;
  };

  const start = () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const master = ctx.createGain();
    master.gain.setValueAtTime(0, ctx.currentTime);
    master.gain.linearRampToValueAtTime(0.05, ctx.currentTime + 3);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 900;
    filter.Q.value = 0.4;
    master.connect(filter);
    filter.connect(ctx.destination);
    const nodes = [];
    CHORD.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.value = i < 2 ? 0.5 : 0.28;
      // slow breathing LFO on each voice
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.04 + i * 0.017;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = i < 2 ? 0.18 : 0.12;
      lfo.connect(lfoGain);
      lfoGain.connect(g.gain);
      // gentle celestial detune drift
      const drift = ctx.createOscillator();
      drift.frequency.value = 0.05 + i * 0.013;
      const driftGain = ctx.createGain();
      driftGain.gain.value = 1.2;
      drift.connect(driftGain);
      driftGain.connect(osc.detune);
      osc.connect(g);
      g.connect(master);
      osc.start(); lfo.start(); drift.start();
      nodes.push(osc, lfo, drift);
    });
    audioRef.current = { ctx, master, nodes };
  };

  const toggle = () => {
    if (playing) {
      stop();
      setPlaying(false);
      localStorage.setItem(PREF_KEY, "0");
    } else {
      start();
      setPlaying(true);
      localStorage.setItem(PREF_KEY, "1");
    }
  };

  // Resume soundtrack on the visitor's first interaction if they had it on
  useEffect(() => {
    if (localStorage.getItem(PREF_KEY) !== "1") return;
    const resume = () => {
      if (!audioRef.current) { start(); setPlaying(true); }
      window.removeEventListener("pointerdown", resume);
    };
    window.addEventListener("pointerdown", resume);
    return () => window.removeEventListener("pointerdown", resume);
  }, []);

  useEffect(() => () => { if (audioRef.current) { try { audioRef.current.ctx.close(); } catch (e) {} } }, []);

  return (
    <button
      data-testid="ambient-sound-toggle"
      onClick={toggle}
      aria-label={playing ? "Mute celestial soundtrack" : "Play celestial soundtrack"}
      title={playing ? "Mute celestial soundtrack" : "Play celestial soundtrack"}
      className={`fixed bottom-24 right-6 z-[85] w-11 h-11 rounded-full flex items-center justify-center border transition-all duration-500 backdrop-blur-md ${
        playing
          ? "border-[rgba(232,213,163,0.55)] bg-[rgba(232,213,163,0.12)] text-[var(--text-accent,#E8D5A3)] shadow-[0_0_24px_rgba(232,213,163,0.35)] animate-pulse"
          : "border-white/15 bg-black/40 text-[var(--text-muted,#8A7CA8)] hover:text-[var(--text-accent,#E8D5A3)] hover:border-[rgba(232,213,163,0.4)]"
      }`}
    >
      {playing ? <Volume2 size={17} /> : <VolumeX size={17} />}
    </button>
  );
};
